/**
 * Quick Actions Grid Component
 * Displays action cards for common tasks
 * Supports compact mode (outlined buttons) for dashboard sidebar
 */
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardActionArea, CardContent, Typography, Grid } from '@mui/material';
import {
  Groups as TeamsIcon,
  Settings as SettingsIcon,
  Approval as ApprovalIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';

interface QuickAction {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
  teamLeadOnly?: boolean;
}

const quickActions: QuickAction[] = [
  {
    title: 'View Teams',
    description: 'Manage your teams and configurations',
    icon: <TeamsIcon sx={{ fontSize: 32, color: '#6366F1' }} />,
    path: '/teams',
  },
  {
    title: 'Pending Approvals',
    description: 'Review team onboarding requests',
    icon: <ApprovalIcon sx={{ fontSize: 32, color: '#F59E0B' }} />,
    path: '/admin/approvals',
    adminOnly: true,
  },
  {
    title: 'Admin Dashboard',
    description: 'System-wide settings and monitoring',
    icon: <SettingsIcon sx={{ fontSize: 32, color: '#8B5CF6' }} />,
    path: '/admin',
    adminOnly: true,
  },
];

interface QuickActionsGridProps {
  compact?: boolean;
}

export default function QuickActionsGrid({ compact = false }: QuickActionsGridProps) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const visibleActions = quickActions.filter((action) => {
    if (action.adminOnly && !user?.isAdmin) return false;
    return true;
  });

  if (compact) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {visibleActions.map((action) => (
            <Button
              key={action.title}
              variant="outlined"
              size="small"
              onClick={() => navigate(action.path)}
              sx={{
                textTransform: 'none',
                borderColor: '#E5E7EB',
                color: 'text.primary',
                fontWeight: 500,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(99, 102, 241, 0.04)',
                },
              }}
            >
              {action.title}
            </Button>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={2}>
        {visibleActions.map((action) => (
          <Grid item xs={12} sm={6} md={4} key={action.title}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardActionArea
                onClick={() => navigate(action.path)}
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
              >
                <CardContent sx={{ width: '100%', p: 2.5 }}>
                  <Box
                    sx={{
                      mb: 2,
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: '#F3F4F6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {action.icon}
                  </Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
